import { useEffect } from "react";
import { IoMdClose } from "react-icons/io";
import {
  FiAlertCircle,
  FiCheck,
  FiX,
  FiTrash2,
  FiLogOut,
} from "react-icons/fi";
import { HiOutlineExclamationTriangle } from "react-icons/hi2";
import { MdOutlineWarning, MdOutlineInfo } from "react-icons/md";

type ModalTheme = "netflix" | "google" | "facebook" | "ios";
type ButtonVariant = "primary" | "secondary" | "danger" | "success" | "warning";
type IconType = "warning" | "info" | "success" | "danger" | "question";

interface ButtonConfig {
  text: string;
  variant: ButtonVariant;
  action: () => void;
  icon?: React.ReactNode;
}

interface ThemeStyles {
  backdrop: string;
  container: string;
  header: string;
  headerTitle: string;
  content: string;
  message: string;
  buttonContainer: string;
  buttons: {
    primary: string;
    secondary: string;
    danger: string;
    success: string;
    warning: string;
  };
}

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  theme?: ModalTheme;
  title?: string;
  message?: string;
  icon?: IconType | React.ReactNode;
  showIcon?: boolean;
  buttons: ButtonConfig[];
  customWidth?: string;
}

export function PopupModalV2({
  open,
  onClose,
  theme = "google",
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  icon = "question",
  showIcon = true,
  buttons,
  customWidth = "max-w-md",
}: ConfirmationModalProps) {
  useEffect(() => {
    if (open) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [open]);

  if (!open) return null;

  const getIcon = (): React.ReactNode => {
    if (typeof icon === "object") return icon;

    switch (icon) {
      case "warning":
        return (
          <HiOutlineExclamationTriangle className="text-4xl text-yellow-600 flex-shrink-0" />
        );
      case "danger":
        return (
          <FiAlertCircle className="text-4xl text-red-600 flex-shrink-0" />
        );
      case "success":
        return <FiCheck className="text-4xl text-green-600 flex-shrink-0" />;
      case "info":
        return (
          <MdOutlineInfo className="text-4xl text-blue-600 flex-shrink-0" />
        );
      case "question":
      default:
        return (
          <MdOutlineWarning className="text-4xl text-gray-600 flex-shrink-0" />
        );
    }
  };

  const getIconContainer = (): string => {
    if (typeof icon === "object") return "bg-gray-50 p-3 rounded-full";

    switch (icon) {
      case "warning":
        return "bg-yellow-50 p-3 rounded-full";
      case "danger":
        return "bg-red-50 p-3 rounded-full";
      case "success":
        return "bg-green-50 p-3 rounded-full";
      case "info":
        return "bg-blue-50 p-3 rounded-full";
      case "question":
      default:
        return "bg-gray-50 p-3 rounded-full";
    }
  };

  const getThemeStyles = (): ThemeStyles => {
    switch (theme) {
      case "netflix":
        return {
          backdrop: "bg-black bg-opacity-60 backdrop-blur-sm",
          container: "bg-white shadow-2xl border border-gray-100 rounded-lg",
          header:
            "bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg",
          headerTitle: "font-bold text-lg",
          content: "bg-white p-6 rounded-b-lg",
          message: "text-gray-700 text-base leading-relaxed",
          buttonContainer: "flex gap-3 mt-6 flex-wrap",
          buttons: {
            primary:
              "bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-md transition-colors duration-200 flex items-center gap-2 justify-center",
            secondary:
              "bg-transparent hover:bg-gray-50 text-gray-600 font-medium py-3 px-6 rounded-md border-2 border-gray-300 hover:border-gray-400 transition-all duration-200",
            danger:
              "bg-red-700 hover:bg-red-800 text-white font-semibold py-3 px-6 rounded-md transition-colors duration-200 flex items-center gap-2 justify-center",
            success:
              "bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-md transition-colors duration-200 flex items-center gap-2 justify-center",
            warning:
              "bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-md transition-colors duration-200 flex items-center gap-2 justify-center",
          },
        };

      case "google":
        return {
          backdrop: "bg-black bg-opacity-40 backdrop-blur-sm",
          container: "bg-white shadow-xl rounded-lg border border-gray-200",
          header:
            "bg-white border-b border-gray-200 text-gray-800 rounded-t-lg",
          headerTitle: "font-medium text-lg",
          content: "bg-white p-6 rounded-b-lg",
          message: "text-gray-600 text-sm leading-relaxed",
          buttonContainer: "flex gap-3 mt-6 justify-end flex-wrap",
          buttons: {
            primary:
              "bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            secondary:
              "bg-transparent hover:bg-gray-50 text-gray-600 font-medium py-2 px-6 rounded text-sm transition-colors duration-200",
            danger:
              "bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            success:
              "bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            warning:
              "bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
          },
        };

      case "facebook":
        return {
          backdrop: "bg-black bg-opacity-50 backdrop-blur-sm",
          container: "bg-white shadow-lg rounded-xl",
          header:
            "bg-white text-gray-800 rounded-t-xl border-b border-gray-100",
          headerTitle: "font-semibold text-lg",
          content: "bg-white p-6 rounded-b-xl",
          message: "text-gray-600 text-base leading-relaxed",
          buttonContainer: "flex gap-3 mt-6 flex-wrap",
          buttons: {
            primary:
              "bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2",
            secondary:
              "bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-lg transition-colors duration-200",
            danger:
              "bg-red-600 hover:bg-red-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2",
            success:
              "bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2",
            warning:
              "bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center gap-2",
          },
        };

      case "ios":
        return {
          backdrop: "bg-black bg-opacity-30 backdrop-blur-md",
          container:
            "bg-white/90 backdrop-blur-xl shadow-2xl rounded-2xl border border-white/20",
          header: "bg-transparent text-gray-800",
          headerTitle: "font-semibold text-lg text-center",
          content: "bg-transparent p-6",
          message: "text-gray-600 text-base leading-relaxed text-center",
          buttonContainer: "flex flex-col gap-2 mt-6",
          buttons: {
            primary:
              "bg-blue-500 hover:bg-blue-600 text-white font-semibold py-4 px-6 rounded-xl text-base transition-colors duration-200 w-full flex items-center gap-2 justify-center",
            secondary:
              "bg-transparent hover:bg-gray-100 text-gray-600 font-medium py-4 px-6 rounded-xl text-base transition-colors duration-200 w-full",
            danger:
              "bg-transparent hover:bg-red-50 text-red-500 font-medium py-4 px-6 rounded-xl text-base transition-colors duration-200 w-full flex items-center gap-2 justify-center",
            success:
              "bg-green-500 hover:bg-green-600 text-white font-semibold py-4 px-6 rounded-xl text-base transition-colors duration-200 w-full flex items-center gap-2 justify-center",
            warning:
              "bg-yellow-500 hover:bg-yellow-600 text-white font-semibold py-4 px-6 rounded-xl text-base transition-colors duration-200 w-full flex items-center gap-2 justify-center",
          },
        };

      default:
        return {
          backdrop: "bg-black bg-opacity-40 backdrop-blur-sm",
          container: "bg-white shadow-xl rounded-lg border border-gray-200",
          header:
            "bg-white border-b border-gray-200 text-gray-800 rounded-t-lg",
          headerTitle: "font-medium text-lg",
          content: "bg-white p-6 rounded-b-lg",
          message: "text-gray-600 text-sm leading-relaxed",
          buttonContainer: "flex gap-3 mt-6 justify-end flex-wrap",
          buttons: {
            primary:
              "bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            secondary:
              "bg-transparent hover:bg-gray-50 text-gray-600 font-medium py-2 px-6 rounded text-sm transition-colors duration-200",
            danger:
              "bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            success:
              "bg-green-600 hover:bg-green-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
            warning:
              "bg-yellow-600 hover:bg-yellow-700 text-white font-medium py-2 px-6 rounded text-sm transition-colors duration-200 flex items-center gap-2",
          },
        };
    }
  };

  const styles = getThemeStyles();

  return (
    <div
      className={`fixed inset-0 z-50 -top-5 flex items-center justify-center ${styles.backdrop}`}
    >
      <div className={`w-11/12 ${customWidth} mx-4 ${styles.container}`}>
        {/* Header */}
        <div
          className={`flex items-center justify-between p-4 ${styles.header}`}
        >
          <h2 className={styles.headerTitle}>{title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-full hover:bg-black/10"
          >
            <IoMdClose className="text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {showIcon && (
            <div
              className={`flex ${
                theme === "ios" ? "flex-col items-center" : "items-start"
              } gap-4 mb-6`}
            >
              <div className={getIconContainer()}>{getIcon()}</div>
              <p className={styles.message}>{message}</p>
            </div>
          )}

          {!showIcon && <p className={`${styles.message} mb-6`}>{message}</p>}

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            {buttons.map((button, index) => (
              <button
                key={index}
                onClick={button.action}
                className={`${styles.buttons[button.variant]} ${
                  theme !== "ios" ? "flex-1 min-w-0" : ""
                }`}
              >
                {button.icon}
                {button.text}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// Button configurations for common scenarios
export const PopupV2ButtonConfigs = {
  // Save Changes scenario
  saveChanges: (
    onSave: () => void,
    onDiscard: () => void,
    onCancel: () => void
  ): ButtonConfig[] => [
    {
      text: "Save Changes",
      variant: "primary",
      action: onSave,
      icon: <FiCheck className="text-sm" />,
    },
    { text: "Don't Save", variant: "danger", action: onDiscard },
    { text: "Cancel", variant: "secondary", action: onCancel },
  ],

  // Delete confirmation
  deleteConfirmation: (
    onDelete: () => void,
    onCancel: () => void
  ): ButtonConfig[] => [
    {
      text: "Delete",
      variant: "danger",
      action: onDelete,
      icon: <FiTrash2 className="text-sm" />,
    },
    { text: "Cancel", variant: "secondary", action: onCancel },
  ],

  // Logout confirmation
  logoutConfirmation: (
    onLogout: () => void,
    onCancel: () => void
  ): ButtonConfig[] => [
    {
      text: "Logout",
      variant: "primary",
      action: onLogout,
      icon: <FiLogOut className="text-sm" />,
    },
    { text: "Cancel", variant: "secondary", action: onCancel },
  ],

  // Yes/No confirmation
  yesNo: (onYes: () => void, onNo: () => void): ButtonConfig[] => [
    { text: "Yes", variant: "primary", action: onYes },
    { text: "No", variant: "secondary", action: onNo },
  ],

  // OK/Cancel
  okCancel: (onOk: () => void, onCancel: () => void): ButtonConfig[] => [
    { text: "OK", variant: "primary", action: onOk },
    { text: "Cancel", variant: "secondary", action: onCancel },
  ],

  // Continue/Go Back
  continueGoBack: (
    onContinue: () => void,
    onGoBack: () => void
  ): ButtonConfig[] => [
    { text: "Continue", variant: "primary", action: onContinue },
    { text: "Go Back", variant: "secondary", action: onGoBack },
  ],

  // Custom single button (for notifications)
  singleButton: (
    text: string,
    action: () => void,
    variant: ButtonVariant = "primary"
  ): ButtonConfig[] => [{ text, variant, action }],
};
